// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  CPAP_BIPAP_USE_ANSWERS,
  NONE_OF_THE_ABOVE_CODE,
  ORAL_APPLIANCE_USE_ANSWERS,
} from '../intake/intake-answer-types';

export interface ExclusionCriterion {
  code: string;
  display: string;
}

const EXCLUSION_CRITERIA_CODES = new Set([
  'J44.9', // Moderate-to-severe COPD or restrictive lung disease
  'I50.9', // Congestive heart failure (NYHA III-IV)
  'G71.9', // Neuromuscular disease affecting respiration
  'I69.30', // History of stroke with residual deficits
  'F11.20', // Chronic opioid use
  'G47.31', // Suspected central sleep apnea
  'G47.36', // Known hypoventilation syndrome
  'I25.110', // Unstable cardiac disease
  'G47.00', // Severe insomnia preventing adequate sleep time
  'G47.50', // Parasomnias or nocturnal seizures
  'previously-inconclusive-failed-hsat',
]);

const YES_NO_EXCLUSIONS: Record<string, string> = {
  'previous-sleep-study': 'Previous HSAT or sleep study',
  'cpap-bipap-use': 'Use of CPAP or BiPAP',
  'oral-appliance-use': 'Use of oral appliance',
  'sleep-apnea-surgery': 'Previous sleep apnea surgery',
  'prior-test-failed': 'Previous sleep test failed or inconclusive',
};

const POSITIVE_YES_NO_ANSWERS = new Set([
  'Yes',
  'Could not tolerate',
  // New intake copy (2026-06 medical-history merge):
  "Yes, I've had an at-home sleep study",
  "Yes, I've had an in-lab sleep study before",
  // Keep recognizing responses submitted before the intake copy update.
  "Yes, I've had an in-home sleep study before",
  'Yes, but I could not tolerate it',
  'Yes, but it was not effective',
  ...CPAP_BIPAP_USE_ANSWERS.filter((answer) => answer.startsWith('Yes')),
  ...ORAL_APPLIANCE_USE_ANSWERS.filter((answer) => answer.startsWith('Yes')),
]);

/**
 * Extracts the selected exclusion criteria from an eligibility-check QR.
 *
 * Scans both the `clinical-conditions` item (coded answers against the ICD
 * allowlist) and the five yes/no items (`previous-sleep-study`,
 * `cpap-bipap-use`, `oral-appliance-use`, `sleep-apnea-surgery`, `prior-test-failed`).
 * For yes/no items, positive answers include "Yes", "Could not tolerate", and
 * new intake-form variants like "Yes, but it was not effective".
 *
 * @returns Array of selected exclusion criteria with their code and display label.
 *          Returns an empty array if none were selected.
 */
export function getExclusionCriteriaFromQR(qr: QuestionnaireResponse): ExclusionCriterion[] {
  const exclusions: ExclusionCriterion[] = [];

  for (const item of qr.item ?? []) {
    if (item.linkId === 'clinical-conditions') {
      for (const answer of item.answer ?? []) {
        const code = answer.valueCoding?.code;
        const display = answer.valueCoding?.display;
        if (code != null && code !== NONE_OF_THE_ABOVE_CODE && display != null && EXCLUSION_CRITERIA_CODES.has(code)) {
          exclusions.push({ code, display });
        }
      }
      continue;
    }

    const display = item.linkId ? YES_NO_EXCLUSIONS[item.linkId] : undefined;
    if (display == null) {
      continue;
    }
    for (const answer of item.answer ?? []) {
      if (answer.valueString != null && POSITIVE_YES_NO_ANSWERS.has(answer.valueString)) {
        exclusions.push({ code: item.linkId as string, display });
        break;
      }
    }
  }

  return exclusions;
}
